import { useMemo, useState } from "react";
import SectionHeader from "./SectionHeader";
import { windFilters, windItems } from "@/pages/home/data/trending";
import type { WindCategory } from "@/pages/home/data/trending";

const dirMeta = {
  up: { icon: "ri-arrow-up-line", cls: "text-accent-600" },
  down: { icon: "ri-arrow-down-line", cls: "text-secondary-700" },
  flat: { icon: "ri-more-line", cls: "text-foreground-400" },
};

const catTone: Record<WindCategory, string> = {
  脱粉: "bg-secondary-100 text-secondary-900",
  回踩: "bg-accent-100 text-accent-900",
  后援会: "bg-primary-100 text-primary-900",
  超话: "bg-primary-100 text-primary-900",
  站姐: "bg-secondary-100 text-secondary-900",
};

export default function Trending() {
  const [active, setActive] = useState<WindCategory | "全部">("全部");
  const list = useMemo(
    () => (active === "全部" ? windItems : windItems.filter((it) => it.cat === active)),
    [active],
  );

  return (
    <section id="trending" className="bg-background-100">
      <div className="mx-auto max-w-[1280px] px-4 py-14 md:px-6 md:py-20">
        <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
          <SectionHeader
            eyebrow="今日风向"
            title="风向热搜榜"
            description="以粉丝反应为核心线索，汇总塌房事件后的讨论热度与立场变化，按反应类型一键筛选。"
          />
          <p className="shrink-0 text-xs text-foreground-600">
            更新于 09:45 · 采样自公开讨论区
          </p>
        </div>

        <div className="mt-10 grid grid-cols-1 gap-8 lg:grid-cols-12 lg:gap-6">
          {/* Editor spotlight */}
          <div className="lg:col-span-5">
            <article className="group flex h-full flex-col overflow-hidden rounded-xl border border-background-200 bg-background-50">
              <div className="relative overflow-hidden">
                <img
                  src="https://readdy.ai/api/search-image?query=Wide%20editorial%20banner%20illustration%20showing%20social%20media%20reaction%20heatmap%20trending%20topic%2C%20warm%20cream%20background%20with%20vermilion%20red%20rising%20bar%20graph%20and%20crimson%20heartbreak%20shapes%2C%20newspaper%20halftone%20texture%2C%20abstract%20vector%20style%2C%20no%20people%2C%20minimal%20modern%20editorial%20look&width=1200&height=675&seq=radar-spotlight-01&orientation=landscape"
                  alt="塌房舆论热度上升曲线的编辑风插画"
                  className="aspect-[16/10] w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                />
                <span className="absolute left-4 top-4 inline-flex items-center gap-1 rounded-full bg-accent-500 px-3 py-1 text-xs font-medium text-background-50">
                  <i className="ri-star-fill text-xs" aria-hidden="true"></i>
                  编辑精选
                </span>
              </div>
              <div className="flex flex-1 flex-col p-5 md:p-6">
                <div className="flex items-center gap-2 text-xs">
                  <span className="text-accent-700">本周选题</span>
                  <span className="text-foreground-400">/</span>
                  <span className="text-foreground-600">粉圈如何消化一次集体失望</span>
                </div>
                <h3 className="mt-3 font-heading text-xl font-bold leading-snug text-foreground-950 md:text-2xl">
                  塌房不是终点，是粉丝重新评估关系的起点
                </h3>
                <p className="mt-3 flex-1 text-sm leading-relaxed text-foreground-700">
                  我们把几十个匿名案例放在一起对比后发现：真正决定粉丝去留的，往往不是爆料本身的严重程度，而是
                  偶像方回应的态度。回应越回避，越容易被解读为心虚，脱粉与回踩的声浪就越集中。
                </p>
                <div className="mt-4 flex items-center justify-between">
                  <a
                    href="#analysis"
                    className="inline-flex items-center gap-1 text-sm font-medium text-primary-700 transition-colors hover:text-primary-800"
                  >
                    阅读深度洞察
                    <i className="ri-arrow-right-line" aria-hidden="true"></i>
                  </a>
                  <span className="text-xs text-foreground-500">8 分钟阅读</span>
                </div>
              </div>
            </article>
          </div>

          {/* Ranked list */}
          <div className="lg:col-span-7">
            {/* filter tabs */}
            <div className="flex flex-wrap items-center gap-2">
              {windFilters.map((f) => {
                const isActive = f.key === active;
                return (
                  <button
                    key={f.key}
                    type="button"
                    onClick={() => setActive(f.key)}
                    className={`whitespace-nowrap rounded-full px-4 py-2 text-sm transition-colors ${
                      isActive
                        ? "bg-primary-500 font-medium text-background-50"
                        : "bg-background-50 text-foreground-700 hover:bg-background-200"
                    }`}
                  >
                    {f.label}
                  </button>
                );
              })}
            </div>

            <ol className="mt-5 divide-y divide-background-200 overflow-hidden rounded-xl border border-background-200 bg-background-50">
              {list.map((item, i) => {
                const dm = dirMeta[item.dir];
                return (
                  <li key={item.id}>
                    <a
                      href="#analysis"
                      className="flex items-start gap-4 px-4 py-4 transition-colors hover:bg-background-100 md:px-5"
                    >
                      <span
                        className={`mt-0.5 w-8 shrink-0 font-heading text-2xl font-bold tabular-nums ${
                          i < 3 ? "text-accent-600" : "text-foreground-300"
                        }`}
                      >
                        {String(i + 1).padStart(2, "0")}
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="flex flex-wrap items-center gap-2">
                          <span className={`inline-flex rounded-full px-2.5 py-0.5 text-xs ${catTone[item.cat]}`}>
                            {item.cat}
                          </span>
                          <span className="text-xs text-foreground-500">{item.time}</span>
                        </span>
                        <h4 className="mt-1.5 text-[15px] font-medium leading-snug text-foreground-950">
                          {item.title}
                        </h4>
                        <p className="mt-1 hidden text-sm leading-relaxed text-foreground-600 sm:block">
                          {item.excerpt}
                        </p>
                      </span>
                      <span className="flex shrink-0 flex-col items-end gap-1">
                        <span className="font-heading text-lg font-bold text-foreground-950">{item.heat}</span>
                        <span className={`inline-flex h-5 w-5 items-center justify-center rounded ${dm.cls}`}>
                          <i className={`${dm.icon} text-sm`} aria-hidden="true"></i>
                        </span>
                      </span>
                    </a>
                  </li>
                );
              })}
              {list.length === 0 && (
                <li className="px-5 py-10 text-center text-sm text-foreground-600">
                  该分类下暂无上榜内容，请查看其它风向。
                </li>
              )}
            </ol>
          </div>
        </div>
      </div>
    </section>
  );
}